import styled from '@emotion/styled';

import {
  getDecorativeImage,
  getDecorativePosition,
} from '@/components/blocks/heroes/CreativeSplitHero/helper';
import { screen } from '@/components/common/breakpoints';
import { Title } from '@/components/common/Title';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { labelMedium } from '@/styles/typography';
import type { BlockWithAudience } from '@/types/shared';

import type { CreativeSplitHero as SanityCreativeSplitHero } from '@/sanity/sanity.types'

export type CreativeSplitHeroProps = SanityCreativeSplitHero &
  BlockWithAudience;
type CreativeSplitHeroContentProps = Omit<CreativeSplitHeroProps, '_type'>;

const CreativeSplitHeroContent = ({
  heading,
  subheading,
  caption,
  pageAudience,
  isAudienceSwitcherEnabled,
}: CreativeSplitHeroContentProps) => {
  return (
    <TextWrapper>
      {!!caption && (
        <CaptionContainer>
          <PortableText
            value={caption}
            audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
          />
        </CaptionContainer>
      )}
      {!!heading && (
        <Title
          size="md"
          variant="hero"
          heading={heading}
          desktopAlignment="start"
          mobileAlignment="start"
        />
      )}
      {!!subheading && (
        <SubheadingWrapper>
          <PortableText
            value={subheading}
            audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
          />
        </SubheadingWrapper>
      )}
    </TextWrapper>
  );
};

export const CreativeSplitHero = ({
  decorative,
  heading,
  subheading,
  caption,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: CreativeSplitHeroProps) => {
  if (!decorative) return null;

  const decorativeImage = getDecorativeImage(decorative);
  const decorativePosition = getDecorativePosition(decorative);
  return (
    <Section
      backgroundColour="--background-subtle"
      className="creative-split-hero"
      spacingMode="margin"
      spacing={blockSpacing}
      verticalPadding={paddingAdjustment ?? true}
    >
      <HeroWrapper>
        <DesktopContainer>
          <CreativeSplitHeroContent
            heading={heading}
            subheading={subheading}
            caption={caption}
            pageAudience={pageAudience}
            isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
          />
          <DecorativeContainerDesktop
            decorativeImage={decorativeImage}
            decorativePosition={decorativePosition}
          />
        </DesktopContainer>
        <MobileContainer>
          <CreativeSplitHeroContent
            heading={heading}
            subheading={subheading}
            caption={caption}
            pageAudience={pageAudience}
            isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
          />
        </MobileContainer>
      </HeroWrapper>
    </Section>
  );
};

const HeroWrapper = styled.div`
  display: flex;
  gap: var(--space-block-md);
  width: 100%;

  ${screen.md} {
    display: flex;
    padding-top: var(--space-page-sm);
    align-items: flex-start;
  }
`;

const DesktopContainer = styled.div`
  display: none;

  ${screen.md} {
    display: flex;
    width: 100%;
    gap: var(--space-block-md);
  }
`;

const DecorativeContainerDesktop = styled.div<{
  decorativeImage: {
    desktop: string;
    smallDesktop: string;
  };
  decorativePosition: {
    desktop: string;
    smallDesktop: string;
  };
}>`
  ${screen.md} {
    display: flex;
    background-image: ${({ decorativeImage }) => decorativeImage.smallDesktop};
    background-position: ${({ decorativePosition }) =>
    decorativePosition.smallDesktop};
    background-repeat: no-repeat;
    background-size: auto;
    width: 100%;
    height: 192px;
    margin-right: -80px;
  }

  ${screen.lg} {
    background-image: ${({ decorativeImage }) => decorativeImage.desktop};
    background-position: ${({ decorativePosition }) =>
    decorativePosition.desktop};
    height: 272px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: var(--space-component-xl);
`;

const SubheadingWrapper = styled.div`
  text-align: left;
  align-self: flex-start;

  p {
    font-size: var(--font-size-4);
    color: var(--text-warm-base);
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 30px;

  ${screen.md} {
    display: none;
  }
`;

const CaptionContainer = styled.div`
  width: 100%;
  text-align: left;

  > p {
    ${labelMedium}
    color: var(--text-warm-subtle);
  }
`;
